import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowUpIcon,
  ArrowDownIcon,
  FunnelIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { walletApi, Transaction, TransactionType, TransactionCategory } from '@/api/walletApi'
import LoadingSpinner from '@/components/ui/LoadingSpinner'

const categoryColors: Record<string, string> = {
  SCHOOL_FEES: 'bg-blue-100 text-blue-800',
  TRANSPORT_FEES: 'bg-purple-100 text-purple-800',
  LIBRARY_FEES: 'bg-indigo-100 text-indigo-800',
  EXAM_FEES: 'bg-pink-100 text-pink-800',
  UNIFORM: 'bg-orange-100 text-orange-800',
  BOOKS: 'bg-teal-100 text-teal-800',
  MEALS: 'bg-yellow-100 text-yellow-800',
  ACTIVITIES: 'bg-green-100 text-green-800',
  TOP_UP: 'bg-emerald-100 text-emerald-800',
  REFUND: 'bg-red-100 text-red-800',
  OTHER: 'bg-gray-100 text-gray-800',
}

function formatCurrency(amount: number, currency: string = 'KES') {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(amount)
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function StudentTransactionsPage() {
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<{
    type?: TransactionType
    category?: TransactionCategory
  }>({})
  const [showFilters, setShowFilters] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['myTransactions', page, filters],
    queryFn: () =>
      walletApi.getTransactions({
        page,
        limit: 20,
        ...filters,
      }),
  })

  const clearFilters = () => {
    setFilters({})
    setPage(1)
  }

  const transactions = data?.data?.transactions || []
  const pagination = data?.data?.pagination || { page: 1, limit: 20, total: 0, pages: 0 }
  const summary = data?.data?.summary || { totalCredits: 0, totalDebits: 0, creditCount: 0, debitCount: 0 }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Transactions</h1>
        <p className="text-gray-600">View your wallet transaction history</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-600">Total Received</p>
          <p className="text-xl font-bold text-green-600">{formatCurrency(summary.totalCredits)}</p>
          <p className="text-xs text-gray-500">{summary.creditCount} deposits</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-600">Total Spent</p>
          <p className="text-xl font-bold text-red-600">{formatCurrency(summary.totalDebits)}</p>
          <p className="text-xs text-gray-500">{summary.debitCount} purchases</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-600">Net Change</p>
          <p className={`text-xl font-bold ${summary.totalCredits - summary.totalDebits >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrency(summary.totalCredits - summary.totalDebits)}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
              showFilters ? 'bg-primary-50 border-primary-500 text-primary-700' : 'border-gray-300 text-gray-700'
            }`}
          >
            <FunnelIcon className="w-5 h-5" />
            Filter
          </button>
          {Object.keys(filters).length > 0 && (
            <button onClick={clearFilters} className="flex items-center gap-2 text-red-600">
              <XMarkIcon className="w-5 h-5" />
              Clear
            </button>
          )}
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={filters.type || ''}
                onChange={(e) => setFilters({ ...filters, type: e.target.value as TransactionType || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Types</option>
                <option value="CREDIT">Received</option>
                <option value="DEBIT">Spent</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={filters.category || ''}
                onChange={(e) => setFilters({ ...filters, category: e.target.value as TransactionCategory || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Categories</option>
                <option value="MEALS">Meals</option>
                <option value="BOOKS">Books</option>
                <option value="ACTIVITIES">Activities</option>
                <option value="LIBRARY_FEES">Library</option>
                <option value="TOP_UP">Top Up</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Transactions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No transactions found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {transactions.map((transaction: Transaction) => (
              <div key={transaction._id} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        transaction.type === 'CREDIT'
                          ? 'bg-green-100 text-green-600'
                          : 'bg-red-100 text-red-600'
                      }`}
                    >
                      {transaction.type === 'CREDIT' ? (
                        <ArrowUpIcon className="w-6 h-6" />
                      ) : (
                        <ArrowDownIcon className="w-6 h-6" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{transaction.description}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            categoryColors[transaction.category] || categoryColors.OTHER
                          }`}
                        >
                          {transaction.category.replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p
                      className={`text-lg font-semibold ${
                        transaction.type === 'CREDIT' ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {transaction.type === 'CREDIT' ? '+' : '-'}
                      {formatCurrency(transaction.amount)}
                    </p>
                    <p className="text-sm text-gray-500">{formatDate(transaction.createdAt)}</p>
                    <p className="text-xs text-gray-400">
                      Balance: {formatCurrency(transaction.balanceAfter)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing {(page - 1) * pagination.limit + 1} to {Math.min(page * pagination.limit, pagination.total)} of {pagination.total}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === pagination.pages}
                className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
